/**
 * @file Overdue date cell for the onboarding / offboarding queues.
 *
 * These queues exist to surface "who is about to fall through". The rows that
 * need eyes first are the ones whose date has already PASSED while work is
 * still open — a joiner already at their desk with no gear, a leaver already
 * gone with a laptop unaccounted for. Both dates rendered as plain text, so
 * those rows looked identical to a joiner starting next month.
 *
 * Deliberately restrained: a muted amber tint and a small "N d ago" suffix,
 * only when work is actually still open. No saturated red, no row fill.
 */

import { el, formatDate } from '../utils.js';

/** Whole days between an ISO/y-m-d date and today (negative = future). */
function daysPast(dateStr) {
  if (!dateStr) return null;
  const then = new Date(dateStr);
  if (Number.isNaN(then.getTime())) return null;
  const startOfToday = new Date();
  startOfToday.setHours(0, 0, 0, 0);
  then.setHours(0, 0, 0, 0);
  return Math.round((startOfToday - then) / 86400000);
}

/**
 * @param dateStr   the queue's date (startDate for joiners, endDate for leavers)
 * @param hasOpen   true when this row still has unfinished work
 * @param pastLabel 'started' | 'left' — wording for the suffix
 */
export function overdueDateCell(dateStr, hasOpen, pastLabel = 'started') {
  const days = daysPast(dateStr);
  const overdue = hasOpen && days != null && days > 0;
  if (!overdue) return el('span', null, formatDate(dateStr));
  return el('span', { class: 'date-overdue', title: `${pastLabel} ${days} day${days === 1 ? '' : 's'} ago and still open` },
    formatDate(dateStr),
    el('span', { class: 'date-overdue-note' }, `${pastLabel} ${days}d ago`),
  );
}
